import { Component, Element, Event, EventEmitter, h, Host, Prop } from "@stencil/core";
import { MessageType, MessageTypes } from "./message-types";

@Component({
    tag: "ux-message-box",
    styleUrl: "message-box.scss",
    shadow: false
})
export class MessageBox {
    private baseClass = "ux-message-box";

    @Element() messageBoxElement!: HTMLUxMessageBoxElement;

    /**
     * Message box type. Possible values are `error`, `info`, `warning`, and `success`.
     * @default MessageTypes.info
     */
    @Prop() readonly type: MessageType = MessageTypes.info;

    /**
     * Intro text that is displayed above the headline.
     * @default ""
     */
    @Prop() readonly intro: string = "";

    /**
     * Headline text.
     * @default ""
     */
    @Prop() readonly headline: string = "";

    /**
     * Icon - set empty string to omit the icon.
     * @default null
     */
    // @ts-expect-error Check for typing
    @Prop() readonly icon: string = null;

    /**
     * Icon size with unit.
     * @default "60px"
     */
    @Prop() readonly iconSize: string = "60px";

    /**
     * Do not show the close button.
     * @default false
     */
    @Prop() readonly noCloseButton: boolean = false;

    /**
     * Emitted before the actual redirect takes place, includes the URL, can be cancelled.
     * @event
     */
    @Event({ eventName: "uxMessageClose", bubbles: true, cancelable: true })
    close!: EventEmitter<HTMLUxMessageBoxElement>;

    /** Close message box on click */
    private closeMessageBox = () => {
        const evt = this.close.emit(this.messageBoxElement);
        if (!evt.defaultPrevented) {
            this.messageBoxElement.remove();
        }
    };

    private getIcon = () => {
        if (this.icon === null) {
            switch (this.type) {
                case MessageTypes.success:
                    return "checkmark-thin";
                case MessageTypes.info:
                    return "info-rounded";
                case MessageTypes.warning:
                    return "warning-triangle-simple";
                case MessageTypes.error:
                    return "close-thin";
            }
        }
        return this.icon;
    };

    render() {
        const { baseClass, type, intro, headline, iconSize, noCloseButton } = this;
        const icon = this.getIcon();

        return (
            <Host class={`${baseClass} ${baseClass}--${type}`} role="alert" aria-live="assertive">
                <div class={`${baseClass}__container`}>
                    {!noCloseButton && (
                        <div
                            class={`${baseClass}__close`}
                            role="button"
                            onClick={this.closeMessageBox}
                            aria-label="Close message box"
                        >
                            <ux-icon name="cross" weight="normal" aria-hidden="true"></ux-icon>
                        </div>
                    )}
                    {icon !== "" && (
                        <div class={`${baseClass}__icon`} aria-hidden="true">
                            <ux-icon name={`${icon}`} style={{ fontSize: iconSize }}></ux-icon>
                        </div>
                    )}
                    <div class={`${baseClass}__text`}>
                        {intro !== "" && <span class={`${baseClass}__text-intro ux-text-status`}>{intro}</span>}
                        {headline !== "" && (
                            <span class={`${baseClass}__text-headline ux-text-title ux-text-title--bold`}>{headline}</span>
                        )}
                        <span class={`${baseClass}__text-text ux-text-normal`}>
                            <slot />
                        </span>
                    </div>
                </div>
            </Host>
        );
    }
}
